import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { fadeInUp, warmEase } from '@/shared/motion/motionPresets';
import type { NavigatorProvider } from './navigator-results.types';

type NavigatorAltsSectionProps = {
  providers: NavigatorProvider[];
  categoryName: string;
  open: boolean;
  onToggle: () => void;
  renderAlt: (provider: NavigatorProvider, index: number) => ReactNode;
};

/**
 * "See N other matches" toggle + the alternate rows. Each row carries a
 * layoutId so a promoted alternate morphs into the featured slot (the
 * wrapping LayoutGroup lives in NavigatorResults).
 */
export const NavigatorAltsSection = ({
  providers,
  categoryName,
  open,
  onToggle,
  renderAlt,
}: NavigatorAltsSectionProps) => {
  if (providers.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium text-sage transition-colors duration-150 ease-out hover:text-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
      >
        {open
          ? `Hide the other ${providers.length} matches`
          : `See ${providers.length} other ${
              providers.length === 1 ? 'match' : 'matches'
            } in ${categoryName}`}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-150 ease-out ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {open &&
        providers.map((provider, index) => (
          <motion.div
            key={provider.id}
            layoutId={provider.id}
            layout
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ delay: index * 0.05, duration: 0.38, ease: warmEase }}
            className="rounded-xl"
          >
            {renderAlt(provider, index)}
          </motion.div>
        ))}
    </>
  );
};
